import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import type { FileItem } from "../../types";
import { SelectedTagsArea } from "./bulk-add-tags/SelectedTagsArea";
import { TagSearchInput } from "./bulk-add-tags/TagSearchInput";
import { AvailableTagsList } from "./bulk-add-tags/AvailableTagsList";
import { useBulkAddTags } from "./bulk-add-tags/useBulkAddTags";

interface BulkAddTagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedFiles: FileItem[];
  onSuccess?: () => void;
}

export function BulkAddTagsDialog({
  open,
  onOpenChange,
  selectedFiles,
  onSuccess,
}: BulkAddTagsDialogProps) {
  const {
    selectedTagIds,
    selectedTags,
    filteredTags,
    tagsLoading,
    searchQuery,
    setSearchQuery,
    handleTagAdd,
    handleTagRemove,
    handleSubmit,
    isPending,
    hasAvailableTags,
  } = useBulkAddTags({ open, onOpenChange, selectedFiles, onSuccess });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle>Add Tags</DialogTitle>
          <DialogDescription>
            Add tags to {selectedFiles.length} selected file(s). Selected tags
            will be added to all selected files.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4 px-6">
          <div className="space-y-4">
            <SelectedTagsArea
              selectedTags={selectedTags}
              onRemove={handleTagRemove}
            />
            <TagSearchInput value={searchQuery} onChange={setSearchQuery} />
          </div>

          <AvailableTagsList
            tags={filteredTags}
            onAdd={handleTagAdd}
            isLoading={tagsLoading}
            searchQuery={searchQuery}
            hasAvailableTags={hasAvailableTags}
          />
        </div>

        <DialogFooter className="p-6 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isPending || tagsLoading || selectedTagIds.size === 0}
          >
            {isPending ? "Adding..." : "Add Tags"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
